import type { TopicDetail } from '@/mocks/topics';

interface TopicBodyProps {
  detail: TopicDetail;
}

const sections = [
  { id: 'background', label: '事件梳理' },
  { id: 'focus', label: '讨论焦点' },
  { id: 'timeline', label: '时间线' },
  { id: 'stances', label: '各方说法' },
  { id: 'editor', label: '编辑按语' },
];

export default function TopicBody({ detail }: TopicBodyProps) {
  return (
    <article className="border-t border-background-200 bg-background-100">
      <div className="w-full px-4 py-12 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <h2 className="font-heading text-sm font-bold text-foreground-950">内容导航</h2>
              <ul className="mt-4 space-y-2 border-l border-background-300">
                {sections.map((section) => (
                  <li key={section.id}>
                    <a
                      href={`#${section.id}`}
                      className="block border-l-2 border-transparent pl-3 text-sm text-foreground-600 transition-colors hover:border-primary-500 hover:text-primary-600"
                    >
                      {section.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <section id="background" className="scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950">事件梳理</h2>
              <p className="mt-4 max-w-3xl text-sm leading-loose text-foreground-700 md:text-base">
                {detail.background}
              </p>
            </section>

            <section id="focus" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950">讨论焦点</h2>
              <ul className="mt-4 space-y-3">
                {detail.focusPoints.map((point, index) => (
                  <li key={point} className="flex items-start gap-3 border border-background-200 bg-background-50 p-4">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center bg-accent-500 font-heading text-xs font-bold text-background-50">
                      {index + 1}
                    </span>
                    <span className="text-sm leading-relaxed text-foreground-700">{point}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section id="timeline" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950">时间线</h2>
              <ol className="mt-5 border-l border-background-300">
                {detail.timeline.map((item) => (
                  <li key={`${item.time}-${item.text}`} className="relative pb-6 pl-6 last:pb-0">
                    <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-primary-500" aria-hidden="true" />
                    <span className="font-heading text-xs font-bold text-primary-600">{item.time}</span>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                  </li>
                ))}
              </ol>
            </section>

            <section id="stances" className="mt-12 scroll-mt-24">
              <h2 className="font-heading text-xl font-bold text-foreground-950">各方说法</h2>
              <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
                {detail.stances.map((stance) => (
                  <div key={stance.party} className="border border-background-200 bg-background-50 p-5">
                    <span className="flex items-center gap-2 font-heading text-sm font-bold text-secondary-800">
                      <i className="ri-chat-quote-line text-secondary-500" aria-hidden="true" />
                      {stance.party}
                    </span>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{stance.content}</p>
                  </div>
                ))}
              </div>
            </section>

            <section id="editor" className="mt-12 scroll-mt-24">
              <div className="border-l-4 border-primary-500 bg-background-50 p-6">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-primary-700">
                  <i className="ri-quill-pen-line" aria-hidden="true" />
                  编辑按语
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-700">{detail.editorNote}</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </article>
  );
}